import type { PlayerSettings } from '../profile/schema';

export type CatchPhase = 'line-out' | 'bite' | 'pull' | 'reel-window' | 'reveal' | 'settled';
/** Origin is required at the app boundary: an offline settle never becomes a ceremony. */
export interface CatchSnapshot { readonly origin: 'live' | 'offline'; readonly catches: Readonly<Record<string, number>>; readonly records: Readonly<Record<string, number>>; }
/**
 * `milestone` is a label the host attaches when the catch that landed also finished a goal,
 * raised the level or reached a mastery rung; it is never set by `detectCatch`. An event that
 * carries one is notable and is revealed in full under every catch-reveal setting.
 */
export interface CatchEvent { readonly atMs: number; readonly speciesId: string; readonly kind: 'ordinary' | 'new' | 'record' | 'new-record'; readonly milestone?: string; }
export interface CatchPresentation { readonly phase: CatchPhase; readonly event: CatchEvent | null; readonly compact: boolean; }
/** The ephemeral ceremony state the shell supplies: never serialized, never read by the sim. */
export interface CatchEphemeral { readonly catch: CatchEvent | null; readonly reeledAtMs: number | null; }

/**
 * THE ONE PHASE TABLE: the millisecond after the catch at which each phase ENDS. The water
 * (render/fishing.ts), the plate (catch-instrument.ts) and the sound (audio/engine.ts) all read
 * their moments from here, so none of them can drift from the model.
 */
export const CATCH_PHASE_END_MS: Readonly<Record<Exclude<CatchPhase, 'settled'>, number>> = { 'line-out': 250, bite: 500, pull: 800, 'reel-window': 1_100, reveal: 4_000 };
const PHASES: readonly Exclude<CatchPhase, 'settled'>[] = ['line-out', 'bite', 'pull', 'reel-window', 'reveal'];

function total(values: Readonly<Record<string, number>>): number { return Object.values(values).reduce((sum, value) => sum + value, 0); }

/** Rate-free DTO comparison: a burst is an offline settle, never a stack of ceremonies. */
export function detectCatch(before: CatchSnapshot, after: CatchSnapshot, atMs: number): CatchEvent | null {
  if (before.origin === 'offline' || after.origin === 'offline') return null;
  if (total(after.catches) - total(before.catches) !== 1) return null;
  const speciesId = Object.keys(after.catches).find(id => (after.catches[id] ?? 0) > (before.catches[id] ?? 0));
  if (speciesId === undefined) return null;
  const record = (after.records[speciesId] ?? 0) > (before.records[speciesId] ?? 0);
  const fresh = (before.catches[speciesId] ?? 0) === 0;
  return { atMs, speciesId, kind: record && fresh ? 'new-record' : record ? 'record' : fresh ? 'new' : 'ordinary' };
}

/** Whether the catch-reveal setting lets a catch of this kind be announced at all. */
export function revealsCatch(setting: PlayerSettings['catchReveal'], kind: CatchEvent['kind']): boolean {
  if (setting === 'all') return true;
  if (setting === 'notable') return kind !== 'ordinary';
  return kind === 'new' || kind === 'new-record';
}

/** Notable: not an ordinary repeat, or an ordinary repeat that carries a milestone. */
function notableEvent(event: CatchEvent): boolean { return event.kind !== 'ordinary' || event.milestone !== undefined; }
/** Revealed: the setting allows the kind, or the event carries a milestone the player must see. */
export function revealsEvent(event: CatchEvent, settings: PlayerSettings): boolean { return event.milestone !== undefined || revealsCatch(settings.catchReveal, event.kind); }

export function catchPresentationAt(event: CatchEvent | null, nowMs: number, settings: PlayerSettings): CatchPresentation {
  if (event === null) return { phase: 'settled', event: null, compact: true };
  const elapsed = Math.max(0, nowMs - event.atMs);
  const notable = notableEvent(event);
  const reveal = revealsEvent(event, settings);
  const e = CATCH_PHASE_END_MS;
  const phase: CatchPhase = elapsed < e['line-out'] ? 'line-out' : elapsed < e.bite ? 'bite' : elapsed < e.pull ? 'pull' : elapsed < e['reel-window'] ? 'reel-window' : elapsed < e.reveal && reveal ? 'reveal' : 'settled';
  return { phase, event, compact: !notable || !reveal };
}

/** The millisecond after the catch at which `phase` begins for this event under these settings. */
export function catchPhaseStartMs(event: CatchEvent | null, phase: CatchPhase, settings: PlayerSettings): number {
  if (phase === 'settled') return event !== null && revealsEvent(event, settings) ? CATCH_PHASE_END_MS.reveal : CATCH_PHASE_END_MS['reel-window'];
  const previous = PHASES[PHASES.indexOf(phase) - 1];
  return previous === undefined ? 0 : CATCH_PHASE_END_MS[previous];
}

/** A presentational acknowledge cannot reach state or persistence. */
export function reelPresentation(model: CatchPresentation, _nowMs: number): CatchPresentation {
  if (model.event === null || (model.phase !== 'pull' && model.phase !== 'reel-window')) return model;
  return { ...model, phase: 'reveal', compact: !notableEvent(model.event) };
}

/** The model with Reel applied at the moment it was pressed: the composition, and the only one. */
export function catchModelAt(nowMs: number, ephemeral: CatchEphemeral, settings: PlayerSettings): CatchPresentation {
  const base = catchPresentationAt(ephemeral.catch, nowMs, settings);
  return ephemeral.reeledAtMs === null ? base : reelPresentation(base, nowMs);
}
